import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import search from 'hooks/grids/search';

import { GRID_PAGE_SIZE } from '../../../constants';
import {
  BonusAdditionalPaymentMetaData,
  BonusExclusionMetaData,
  BonusOverrideMetaData,
} from '../BonusOverrideIndicator';

// Hard stop on paging so a runaway response can't loop forever.
const MAX_PAGES = 20;

export interface OverrideMarkers {
  _overrides: BonusOverrideMetaData[];
  _exclusions: BonusExclusionMetaData[];
  _additionalPayments: BonusAdditionalPaymentMetaData[];
}

/**
 * Groups weekly bonus docs' override enrichment (`_overrides`, `_exclusions`,
 * `_additionalPayments`, `reasonCode`) by driver, tagging each entry with the sub-period (week) its doc
 * falls in. Each doc's row-level `reasonCode` is folded into that doc's own
 * entries, since reason codes can't be merged across weeks on one row.
 */
export const groupOverrideMarkers = ({
  docs,
  entityField,
  subPeriods,
}: {
  docs: ElasticDocument[];
  entityField: string;
  subPeriods: Period[];
}): Record<string, OverrideMarkers> => {
  const byDriver: Record<string, OverrideMarkers> = {};

  docs.forEach((doc) => {
    const driver = doc[entityField];
    if (typeof driver !== 'string') {
      return;
    }
    const overrides: BonusOverrideMetaData[] = Array.isArray(doc['_overrides'])
      ? doc['_overrides']
      : [];
    const exclusions: BonusExclusionMetaData[] = Array.isArray(
      doc['_exclusions'],
    )
      ? doc['_exclusions']
      : [];
    const additionalPayments: BonusAdditionalPaymentMetaData[] = Array.isArray(
      doc['_additionalPayments'],
    )
      ? doc['_additionalPayments']
      : [];
    if (
      overrides.length === 0 &&
      exclusions.length === 0 &&
      additionalPayments.length === 0
    ) {
      return;
    }

    const date = doc['date'];
    const periodLabel =
      typeof date === 'string'
        ? subPeriods.find((sp) => date >= sp.startDate && date <= sp.endDate)
            ?.label
        : undefined;
    const reasonCodes: string[] | undefined =
      Array.isArray(doc['reasonCode']) && doc['reasonCode'].length > 0
        ? doc['reasonCode']
        : undefined;

    const markers = byDriver[driver] ?? {
      _overrides: [],
      _exclusions: [],
      _additionalPayments: [],
    };
    markers._overrides.push(
      ...overrides.map(
        (o): BonusOverrideMetaData => ({
          ...o,
          periodLabel,
          reasonCodes: reasonCodes ?? o.reasonCodes,
        }),
      ),
    );
    markers._exclusions.push(...exclusions.map((e) => ({ ...e, periodLabel })));
    markers._additionalPayments.push(
      ...additionalPayments.map((p) => ({ ...p, periodLabel })),
    );
    byDriver[driver] = markers;
  });

  return byDriver;
};

/**
 * Loads every weekly doc in the sign-off period (only the fields the override
 * indicator needs) and groups their override enrichment by driver. Aggregated
 * sign-off rows don't carry that enrichment, so this backfills it.
 */
const fetchPeriodOverrideMarkers = async ({
  client,
  dataType,
  filters,
  dateScope,
  entityField,
  subPeriods,
}: {
  client: ApolloClient<NormalizedCacheObject>;
  dataType: string;
  filters: FilterInput[];
  dateScope: DateRangeInput;
  entityField: string;
  subPeriods: Period[];
}): Promise<Record<string, OverrideMarkers>> => {
  const docs: ElasticDocument[] = [];
  let searchAfter: string | undefined;

  for (let page = 0; page < MAX_PAGES; page++) {
    const result = await search({
      dataType,
      filters,
      dateScope,
      sortBy: [{ field: 'date', sort: 'desc' }],
      client,
      searchAfter,
      includeFields: [
        entityField,
        'date',
        '_overrides',
        '_exclusions',
        '_additionalPayments',
        'reasonCode',
      ],
    });
    docs.push(...result.documents);
    if (
      result.documents.length < GRID_PAGE_SIZE ||
      !result.searchAfter ||
      docs.length >= result.count
    ) {
      break;
    }
    searchAfter = result.searchAfter;
  }

  return groupOverrideMarkers({ docs, entityField, subPeriods });
};

export default fetchPeriodOverrideMarkers;
